import * as _ from 'lodash';
import HierarchyBuilder from './HierarchyBuilder';
import Scorer from './Scorer';

class ModeDataComposer {

    constructor(rawData) {
        this.composedData = this._composeData(rawData);
    }

    getData() {
        return this.composedData;
    }

    _composeData(rawData) {
        console.time("ModeDataManager._composeData");

        const dates = _.sortBy(this._getDataset(rawData, "dates", false), x => x.date);
        const accounts = this._getDataset(rawData, "csm_accounts", false);
        const activeUsers = this._getDataset(rawData, "active_users", true);
        const visitsWeekly = this._getDataset(rawData, "billable_visits_weekly", true);
        const serverImpressionsWeekly = this._getDataset(rawData, "server_impressions_weekly", true);
        const appSessionsWeekly = this._getDataset(rawData, "app_sessions_weekly", true);
        const tagsWeekly = this._getDataset(rawData, "tagged_items_weekly", true);
        const productCatalogs = this._getDataset(rawData, "product_catalogs", true);
        const revenueConnections = this._getDataset(rawData, "revenue_connections", true);
        const opps = this._getDataset(rawData, "opps", true);

        const cd = _.map(accounts, acct => {
            const o = {};
            o.account = acct;
            this._addOppsInfoToAccount(o.account, opps[acct.client_id] ? opps[acct.client_id] : []);
            o.product_catalogs = {};
            o.product_catalogs.catalogs = productCatalogs[acct.client_id] ? productCatalogs[acct.client_id] : [];
            o.revenue_connections = {};
            o.revenue_connections.connections = revenueConnections[acct.client_id] ? revenueConnections[acct.client_id] : [];
            o.visits = this._getVisitsTimeSeries(dates, visitsWeekly[acct.client_id], serverImpressionsWeekly[acct.client_id]);
            o.app_sessions = this._getTimeSeries(dates, appSessionsWeekly[acct.client_id], "sessions");
            o.tagged_items = this._getTimeSeries(dates, tagsWeekly[acct.client_id], "items");
            o.active_users = {};
            o.active_users.count = activeUsers[acct.client_id][0].num_active_users;

            return o;
        });

        const scorer = new Scorer();
        scorer.addScoreData(cd);

        const accountData = rawData.account_hierarchy;
        const hierarchyBuilder = new HierarchyBuilder();
        hierarchyBuilder.addHierarchyData(cd, accountData);

        console.timeEnd("ModeDataManager._composeData");

        return cd;
    }

    // derive info from opps data and add to account
    _addOppsInfoToAccount(account, opportunities) {

        // the uniqBy is necessary because some brands are attached to opportunities multiple times (i.e. multiple
        // brand opps tying together the same client and opp). The clientValue calcs will be a bit wrong if there are
        // dups. One option is to switch to using the unique list of client IDs to determine the actual number of
        // brands.
        const opps = _.chain(opportunities).uniqBy(o => o.opp_id);
        const openOpps = opps.filter(x => x.stage_name.toLowerCase().indexOf("closed") === -1);
        const openRenewals = openOpps.filter(x => x.type.toLowerCase().indexOf("renewal") > -1);
        const closedWonOpps = opps.filter(x => x.stage_name.toLowerCase().indexOf("closed won") > -1);

        const startDate = closedWonOpps.map(x => x.start_date).sort().head().value();
        const daysSinceStart = (Date.now() - Date.parse(startDate)) / (86400 * 1000);


        //Client ARR for a given op = (ARR of opp/num clients associated with the opp)
        //Client ARR = sum(client ARR for all open renewal/upsell ops)
        const clientValue = openRenewals.map(opp => opp.arr_master / opp.opp_count_brand_opps).sum().value();
        const totalValue = openRenewals.map(opp => opp.arr_master).sum().value();
        const nextRenewal = openRenewals.sortBy(r => r.start_date).head().value();

        const sfBase = "https://curalate.my.salesforce.com/";

        let renewalDate = null;
        let renewalStage = null;
        let renewalLink = null;
        let renewalClients = null;
        let isRedAccount = false;
        let redAccountTrigger = null;
        let redAccountNotes = null;

        if (nextRenewal) {

            renewalDate = nextRenewal.start_date;
            renewalStage = nextRenewal.stage_name;

            const renewalId = nextRenewal.opp_id;
            renewalLink = renewalId ? sfBase + renewalId : null;

            const renewalClientsStr = nextRenewal.brand_opps_client_ids;
            renewalClients = renewalClientsStr ? renewalClientsStr.split(",").map(x => x.trim()) : null;

            redAccountTrigger = nextRenewal.red_account_trigger;
            redAccountNotes = nextRenewal.red_account_notes;
            isRedAccount = redAccountTrigger ? redAccountTrigger !== "Healthy Client" : false;
        }

        account.start_date = startDate;
        account.days_since_start = daysSinceStart;
        account.opps_all = opportunities;
        account.arr_prorated = clientValue;
        account.arr_total = totalValue;
        account.renewal_date = renewalDate;
        account.renewal_stage = renewalStage;
        account.renewal_link = renewalLink;
        account.renewal_clients = renewalClients;
        account.is_red_account = isRedAccount;
        account.red_account_trigger = redAccountTrigger;
        account.red_account_notes = redAccountNotes;
        account.account_link = sfBase + account.sf_account_id;
        account.parent_link = account.parent_id ? sfBase + account.parent_id : null;
    }

    // sets up the time series data for all types of visits - each individual touchpoint and "all", which is simply
    // the sum
    _getVisitsTimeSeries(dates, rawWeekly, siWeekly) {
        const allWeekly = this._sumVisitsAcrossTouchpoints(rawWeekly);
        const weeklyByTouchpoint = this._getVisitsByTouchpoint(rawWeekly);

        const visits = {};
        visits.likely_api_consumer = false;
        visits.all = this._getTimeSeries(dates, allWeekly, "visits", "All");
        const keys = ["Fanreel", "Like2Buy", "Reveal", "Showroom", "Tilt", "Trending Products"];

        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            const formattedKey = key.toLowerCase().replace(" ", "_");
            visits[formattedKey] = this._getTimeSeries(dates, weeklyByTouchpoint[key], "visits", key);
        }

        // figure out if they are likely an API client. This logic only cares about Fanreel | API. and not Fanreel & API.
        const si = this._getTimeSeries(dates, siWeekly, "server_impressions", "API Server Impressions");
        const frLastWeek = visits.fanreel.weekly.current;
        const siLastWeek = si.weekly.current;

        // the ratio of server impressions to fanreel visits varies widely for Fanreel clients because of PDP misses, etc.
        // If there are over 500 server impressions for every 1 visit, it's a safe bet that it's an API client
        if (siLastWeek / (frLastWeek + 1) >= 500) {
            visits.likely_api_consumer = true;
            visits.api_impressions = si;
        }

        return visits;
    }

    _getVisitsByTouchpoint(rawData) {
        return _.groupBy(rawData, function(item) {
            return item.source_type;
        });
    }

    _sumVisitsAcrossTouchpoints(rawData) {
        const groupedByDate = _.groupBy(rawData, function(i) {
            return i.date;
        });

        return _.map(groupedByDate, function(rowsForDate) {
            const totalCount = _.reduce(rowsForDate, function(memo, newItem) {
                return memo + newItem.visits;
            }, 0);
            const p = rowsForDate[0];
            const r = {};
            r.date = p.date;
            r.source_type = "all";
            r.visits = totalCount;
            return r;
        });
    }

    _getTimeSeries(dates, rawWeekly, metricKey, name) {
        const o = {};

        o.metric_key = metricKey;

        if (name) {
            o.name = name;
        }

        // to keep data size down, the rawWeekly data might not contain all the dates in the date set we care about.
        // add zeros where needed
        const weekly = dates.map(d => {
            const o = {};
            o.date = d.date;
            const m = _.find(rawWeekly, x => x.date === o.date);
            o[metricKey] = m ? m[metricKey] : 0;
            return o;
        });

        o.weekly = this._getDeltas(weekly, metricKey);
        o.weekly.raw_data = weekly;

        return o;
    }


    _getDeltas(rawData, metricKey) {
        // input is assumed to be sorted asc
        const desc = rawData.slice().reverse();
        const delta = {};
        delta.current = 0;
        delta.previous = 0;
        delta.delta = 0;
        delta.percentage_delta = 0;

        // for brand new clients, length could be 0
        // for newish clients, length could be 1
        if (desc.length >= 1) {
            delta.current = desc[0][metricKey];
        }

        if (desc.length >= 2) {
            delta.previous = desc[1][metricKey];
        }

        delta.delta = delta.current - delta.previous;
        if (delta.previous !== 0) {
            delta.percentage_delta = Math.round(delta.delta / delta.previous * 100);
        } else if (delta.previous === 0 && delta.current > 0) {
            delta.percentage_delta = Infinity;
        }

        // this is not intended to be long-lived. It's simply a port of the previous monthly trend data.
        // This should be replaced soon with a more intelligent trendline
        delta.four_week_current = 0;
        delta.four_week_previous = 0;
        delta.four_week_delta = 0;
        delta.four_week_percentage_delta = 0;

        for (let i = 0; i < desc.length && i < 4; i++) {
            delta.four_week_current += desc[i][metricKey];
        }

        for (let i = 4; i < desc.length && i < 8; i++) {
            delta.four_week_previous += desc[i][metricKey];
        }

        delta.four_week_delta = delta.four_week_current - delta.four_week_previous;
        if (delta.four_week_previous !== 0) {
            delta.four_week_percentage_delta = Math.round(delta.four_week_delta / delta.four_week_previous * 100);
        } else if (delta.four_week_previous === 0 && delta.four_week_current > 0) {
            delta.four_week_percentage_delta = Infinity;
        }

        return delta;
    }

    _getDataset(rawData, name, groupByClient) {
        const x = rawData[name];

        if (groupByClient) {
            return _.groupBy(x, row => {
                if (row.client_id === undefined) {
                    console.log("client_id not defined on " + row);
                }
                return row.client_id;
            })
        } else {
            return x;
        }
    }
}

export default ModeDataComposer;


// WEBPACK FOOTER //
// ./src/ModeDataComposer.js